import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import PDFObject from "pdfobject";

const HELP_MODAL_STYLES = {
  modalContent: {
    borderRadius: "24px",
    overflow: "hidden",
    border: "1px solid rgba(226, 232, 240, 1)",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)"
  },
  modalDialog: {
    maxWidth: "1080px"
  },
  modalHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
  },
  headerContent: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px"
  },
  modalFooter: {
    padding: "16px 22px 20px",
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    background: "#ffffff"
  },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  closeAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "20px",
    boxShadow: "none"
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#4f46e5",
    marginBottom: "4px"
  },
  title: {
    fontSize: "21px",
    fontWeight: 600,
    color: "#0f172a",
    lineHeight: 1.1,
    margin: 0
  },
  subtitle: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px"
  },
  closeButton: {
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0
  },
  modalBody: {
    padding: "20px 22px 22px 22px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    minHeight: "360px",
    maxHeight: "calc(100vh - 180px)",
    overflowY: "auto",
    overflowX: "hidden"
  },
  helperCard: {
    borderRadius: "16px",
    padding: "16px",
    background: "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(239,246,255,0.9) 100%)",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    marginBottom: "16px"
  },
  helperTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "8px"
  },
  helperText: {
    fontSize: "12px",
    lineHeight: 1.7,
    color: "#64748b",
    margin: 0
  },
  contentCard: {
    borderRadius: "20px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
    padding: "20px"
  },
  supportHeading: {
    fontSize: "26px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "14px"
  },
  supportText: {
    fontSize: "14px",
    lineHeight: 1.8,
    color: "#475569",
    marginBottom: "12px"
  },
  supportSignature: {
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid rgba(226, 232, 240, 1)",
    color: "#0f172a",
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: 1.7
  },
  pdfFrame: {
    height: "600px",
    borderRadius: "18px",
    overflow: "hidden",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#f8fafc"
  },
  errorState: {
    borderRadius: "18px",
    border: "1px solid rgba(254, 202, 202, 1)",
    background: "linear-gradient(180deg, rgba(254,242,242,0.95) 0%, rgba(255,255,255,1) 100%)",
    padding: "32px 24px",
    textAlign: "center",
    color: "#b91c1c"
  }
};

const Help = (props) => {
  const { helpShowHide, handleHelpShowHide, module, helpDocs } = props;
  const [pdfViewUrl, setPdfViewUrl] = useState("");
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    let objectUrl = "";
    let isActive = true;

    if (module === "Support") return undefined;

    const helpDocUrl =
      typeof helpDocs === "string" ? helpDocs : helpDocs?.uploadUrl;
    if (!helpDocUrl) {
      setPdfViewUrl("");
      setPdfError(null);
      return undefined;
    }

    const loadPdfAsBlobUrl = async () => {
      try {
        const response = await fetch(helpDocUrl);
        if (!response.ok) {
          const errorText = await response.text();
          if (response.status === 403 || errorText.includes("AuthenticationFailed")) {
            setPdfError("Unable to load help document. The document link has expired. Please contact support.");
            return;
          }
          throw new Error("Failed to fetch help document");
        }

        const fileBlob = await response.blob();
        const pdfBlob =
          fileBlob.type === "application/pdf"
            ? fileBlob
            : new Blob([fileBlob], { type: "application/pdf" });

        objectUrl = URL.createObjectURL(pdfBlob);
        if (isActive) {
          setPdfViewUrl(objectUrl);
          setPdfError(null);
        }
      } catch (error) {
        if (isActive) {
          if (error.message.includes("AuthenticationFailed") || error.message.includes("403")) {
            setPdfError("Unable to load help document. The document link has expired. Please contact support.");
          } else {
            setPdfError("Failed to load help document. Please try again later.");
          }
        }
      }
    };

    loadPdfAsBlobUrl();

    return () => {
      isActive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [helpDocs, module]);

  useEffect(() => {
    if (module === "Support" || !pdfViewUrl) return;

    const options = {
      pdfOpenParams: {
        toolbar: "0",
        navpanes: "0",
        scrollbars: "0",
        print: "false",
        download: "false"
      }
    };

    PDFObject.embed(pdfViewUrl, "#pdf-viewer", options);
    if (document.getElementById("pdf-viewer")) {
      document.getElementById("pdf-viewer").style.width = "100%";
      document.getElementById("pdf-viewer").style.margin = "0 auto";
    }
  }, [pdfViewUrl, module]);

  return (
    <Modal
      show={helpShowHide}
      size="xl"
      centered
      onHide={handleHelpShowHide}
      dialogClassName="help-modal-dialog"
      contentClassName="help-modal-content"
    >
      <style>
        {`
          .help-modal-dialog {
            max-width: ${HELP_MODAL_STYLES.modalDialog.maxWidth};
          }
          .help-modal-content {
            border-radius: ${HELP_MODAL_STYLES.modalContent.borderRadius};
            overflow: ${HELP_MODAL_STYLES.modalContent.overflow};
            border: ${HELP_MODAL_STYLES.modalContent.border};
            box-shadow: ${HELP_MODAL_STYLES.modalContent.boxShadow};
          }
        `}
      </style>
      <Modal.Header style={HELP_MODAL_STYLES.modalHeader}>
        <div style={HELP_MODAL_STYLES.headerContent}>
          <Modal.Title as="div" style={HELP_MODAL_STYLES.titleWrap}>
            <div style={HELP_MODAL_STYLES.headerIcon}>
              <i
                className={`fa ${module === "Support" ? "fa-life-ring" : "fa-question-circle"}`}
                aria-hidden="true"
              ></i>
            </div>
            <div>

              <div style={HELP_MODAL_STYLES.title}>
                {module === "Support" ? "Add Issue" : "Help"}
              </div>
              <div style={HELP_MODAL_STYLES.subtitle}>
                {module === "Support"
                  ? "Review support guidance before submitting your issue."
                  : "View the attached help document directly inside the popup."}
              </div>
            </div>
          </Modal.Title>
          <button
            type="button"
            style={HELP_MODAL_STYLES.closeButton}
            onClick={handleHelpShowHide}
            aria-label="Close help popup"
          >
            <i className="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body style={HELP_MODAL_STYLES.modalBody}>
        <div style={HELP_MODAL_STYLES.helperCard}>
          <div style={HELP_MODAL_STYLES.helperTitle}>
            {module === "Support" ? "Before you submit an issue" : "Document preview"}
          </div>
          <p style={HELP_MODAL_STYLES.helperText}>
            {module === "Support"
              ? "Our support team reviews each request carefully and keeps you updated on the action taken. Sharing clear issue details helps us resolve things faster."
              : "If the help file loads successfully, you can check complete guide and instructions in the PDF viewer below. If you encounter an error, please contact support for assistance."}
          </p>
        </div>
        {module === "Support" ? (
          <div style={HELP_MODAL_STYLES.contentCard}>
            <div style={HELP_MODAL_STYLES.supportHeading}>
              Welcome to our Customer Support Portal
            </div>
            <p style={HELP_MODAL_STYLES.supportText}>
              This portal gives you a simple way to register any issue you may encounter while
              working with different functions and features.
            </p>
            <p style={HELP_MODAL_STYLES.supportText}>
              Once you submit an issue, it is routed to our dedicated customer and technical
              support team for review so they can provide a fix or next steps as quickly as
              possible.
            </p>
            <p style={HELP_MODAL_STYLES.supportText}>
              Our team will also keep you informed about the action taken and any progress on your
              request.
            </p>
            <div style={HELP_MODAL_STYLES.supportSignature}>
              Thank you.
              <br />
              WorkFINITY
              <br />
              Customer and Technical Support
            </div>
          </div>
        ) : pdfError ? (
          <div style={HELP_MODAL_STYLES.errorState}>
            <i className="fa fa-exclamation-triangle fa-3x mb-3"></i>
            <p style={{ margin: 0 }}>{pdfError}</p>
          </div>
        ) : (
          <div id="pdf-viewer" style={HELP_MODAL_STYLES.pdfFrame}></div>
        )}
      </Modal.Body>
      <Modal.Footer style={HELP_MODAL_STYLES.modalFooter}>
        <div style={HELP_MODAL_STYLES.footerActions}>
          <button
            type="button"
            style={HELP_MODAL_STYLES.closeAction}
            onClick={handleHelpShowHide}
          >
            <i className="fa fa-times" aria-hidden="true"></i>
            Close
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default Help;
